/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import {
  AppRouter,
  DateTimeTypes,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData,
  NumberTypes,
  UserContext,
} from '@gh/common-shared';
import { ActivatedRoute } from '@angular/router';
import { filterTracker, RouteDataService, SlicedDataSource, SortDirection } from '@gh/core-data';
import {
  buildDateTimeColumn,
  buildNumberColumn,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import { BuilderNavigationService, FilterNames, FilterRestService } from '@gh/builder-shared';
import { RfqManagementListBusinessService } from '../../resources/rfq-management-list-business.service';
import { QuoteListFilter, QuoteListItem } from '../../../leads/resources/quote.model';
import { rendererIconByName, truthyCellRenderer } from '@gh/common-internal-shared';
import { BuilderPermissionSet } from '@gh/builder-shared-data';
import { DialogService } from '@gh/core-ui';
import {
  isSalespersonSelectItemInactive,
  QuoteCloneDataDialogConfig,
  quoteCloneDataDialogRoute,
  QuoteCloneDialogEntryPoint,
  SalespersonSelectItem,
} from '../../../leads';
import { MatSelectChange } from '@angular/material/select';
import { filter$ } from '@gh/rx/operators';

@Component({
  standalone: false,
  selector: 'gh-rfq-management-list',
  templateUrl: 'rfq-management-list.component.html',
  styleUrls: ['rfq-management-list.component.scss'],
})
export class RfqManagementListComponent implements OnInit {
  dataSource: SlicedDataSource<any>;
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor({ page: 0, pageSize: -1 , sort: { field: 'updatedDate', direction: SortDirection.Descending }});
  form: FilterableFormState<QuoteListFilter>;
  defaultSalespersonSelectValue = '_All';
  salesPersonsList: any;
  selectedSalespersonId: string = '';

  get isSalespersonListExist(): boolean {
    return this.salesPersonsList.length;
  }

  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private businessService: RfqManagementListBusinessService,
              private router: AppRouter,
              private dialogService: DialogService,
              private navigationService: BuilderNavigationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private userContext: UserContext,
              private filterService: FilterRestService,
              private formStateBuilder: FormStateBuilder) {
    this.routeData.mergeQueryParams({ sortDirection: 'desc', sortField: 'updatedDate'});
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        updatedDate: {
          ...buildDateTimeColumn(DateTimeTypes.DateTime),
          sort: 'desc',
          comparator: () => 0,
        },
        addedDate: buildDateTimeColumn(DateTimeTypes.DateTime),
        isApproved: {
          cellRenderer: truthyCellRenderer(rendererIconByName('done')),
        },
        rfqPrice: buildNumberColumn(NumberTypes.Price),
      },
      {
        getRowId: (params) => String(params.data.quoatationId)
      });
  }

  ngOnInit(): void {
    const {filter: initFilter, gridLayout, salesPersonsList} = getRouteInitData(this.route);
    let isInit = true;
    this.gridDefinition.bind(gridLayout);
    this.salesPersonsList = salesPersonsList;
    if (initFilter.salesPersonId) {
      this.selectedSalespersonId = initFilter.salesPersonId;
      this.defaultSalespersonSelectValue = initFilter.salesPersonId;
    }
    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: { ...initFilter },
      children: {
        searchString: void 0,
        showInactive: void 0,
        showActive: void 0,
        showCompleted: void 0,
        showApprovedOnly: void 0,
        salesPersonId: void 0,
      },
    }));

    const tracker = this.routeData
      .tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      filter.salesPersonId = this.selectedSalespersonId;
      if (!isInit) {
        this.filterService.update(FilterNames.quotationList, filter).subscribe();
      }
      isInit = false;
      this.dataSource = this.businessService.findAllAsDataSource(filter);
    });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  goToAel(quote?: QuoteListItem): void {
    this.navigationService.goToAel({
     label: 'lbl_rfq_management',
     entityType: 'SALES_FOLDER',
     entityId: quote ? quote.salesFolderId : undefined,
   });
  }

  onEdit(quote: QuoteListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Sales.Leads.EDIT_VIEW)) {
      this.router
        .navigateToChild([`/sales/sales-folders/${quote.salesFolderId}/rfq/quotations/${quote.quoatationId}`], {
        parentTitle: 'lbl_rfq_management',
      });
    }
  }

  onClone(quote: QuoteListItem): void {
    this.dialogService.openWithInput(quoteCloneDataDialogRoute, <QuoteCloneDataDialogConfig>{
      contactId: '',
      customerName: '',
      quotationId: quote.quoatationId,
      entryPoint: QuoteCloneDialogEntryPoint.CloneQuoteFromRfq,
      salesFolderId: '',
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((data: any) =>
       this.router.navigateToChild([
         '/sales/sales-folders',
         data.salesFolderId,
         'rfq',
         'quotations',
         data.quotationId,
       ]));
  }

  onSalespersonInfoChange({value}: MatSelectChange): void {
    this.defaultSalespersonSelectValue = value;
    this.selectedSalespersonId = value === '_All' ? '' : value;
    this.form.value.salesPersonId = value === '_All' ? '' : value;
    this.form.emitFilter();
  }

  isSalespersonInactive(salesperson: SalespersonSelectItem): boolean {
    return isSalespersonSelectItemInactive(salesperson);
  }

  onCreateQuote(): void {
    this.dialogService.openWithInput(quoteCloneDataDialogRoute, {
      contactId: '',
      customerName: '',
      entryPoint: QuoteCloneDialogEntryPoint.CreateQuote,
      salesFolderId: '',
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((data: any) =>
       this.router.navigateToChild([
         '/sales/sales-folders',
         data.salesFolderId,
         'rfq',
         'quotations',
         'new',
       ],                          {
         queryParams: {
           siteId: data.siteId,
         },
       }));
  }

}
